// =============================================
// PaymentModal - Interface de paiement tactile professionnelle
// =============================================
// Système de caisse avec:
// - Paiement multi-méthode (espèces, carte, mobile money)
// - Calcul automatique en temps réel
// - Boutons rapides pour espèces
// - Validation bloquée si paiement incomplet
// - Monnaie calculée uniquement sur espèces
// - Sauvegarde automatique des montants (persistance)
// =============================================

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import {
  X, Banknote, CreditCard, Smartphone, Calculator,
  CheckCircle, AlertCircle, Loader2, ArrowLeft, Trash2, Save
} from 'lucide-react';
import { cn } from '../lib/utils';
import axios from 'axios';

// API helper
const api = () => {
  const token = localStorage.getItem('token');
  return axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL || '',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  });
};

// Boutons rapides pour espèces
const QUICK_AMOUNTS = [500, 1000, 2000, 5000, 10000, 20000];

// Format prix CFA
const formatPrice = (amount) => {
  return new Intl.NumberFormat('fr-FR').format(Math.round(amount)) + ' F';
};

// Debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
};

const PaymentModal = ({
  open,
  onClose,
  table,
  tableDetails,
  onPaymentSuccess
}) => {
  // États du paiement
  const [loading, setLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [paymentSession, setPaymentSession] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // Montants saisis
  const [cashAmount, setCashAmount] = useState(0);
  const [cardAmount, setCardAmount] = useState(0);
  const [mobileAmount, setMobileAmount] = useState(0);
  const [notes, setNotes] = useState('');
  
  // Ref pour éviter la sauvegarde initiale
  const isInitialMount = useRef(true);
  const lastSavedRef = useRef({ cash: 0, card: 0, mobile: 0 });

  // Debounce les montants pour la sauvegarde (1 seconde)
  const debouncedCash = useDebounce(cashAmount, 1000);
  const debouncedCard = useDebounce(cardAmount, 1000);
  const debouncedMobile = useDebounce(mobileAmount, 1000);

  // Totaux calculés
  const totals = useMemo(() => {
    // Le total de la commande vient de la session de paiement OU des détails de la table
    // IMPORTANT: On veut le TOTAL DE LA COMMANDE, pas le "remaining" qui peut être 0
    // si un paiement précédent (non finalisé) a été enregistré
    
    // 1. D'abord essayer le total de la session (qui est le vrai total de la commande)
    const sessionOrderTotal = paymentSession?.totals?.total;
    
    // 2. Sinon, utiliser le total des détails de la table
    const detailsTotal = tableDetails?.total ?? tableDetails?.sent_total ?? 0;
    
    // 3. Le total de la commande est le max entre ces deux valeurs
    // (pour éviter d'afficher 0 si une valeur est manquante)
    const orderTotal = Math.max(sessionOrderTotal ?? 0, detailsTotal);
    
    // 4. Calculer ce qui a déjà été payé (paiements finalisés)
    const alreadyPaid = paymentSession?.totals?.already_paid ?? 0;
    
    // 5. Calculer le total à payer maintenant (ce qu'on entre dans les champs)
    const totalPaid = cashAmount + cardAmount + mobileAmount;
    
    // 6. Le restant est le total de la commande moins ce qui a été payé (finalisé + maintenant)
    const remaining = Math.max(0, orderTotal - alreadyPaid - totalPaid);
    
    // 7. Monnaie = uniquement sur espèces, après déduction carte/mobile
    const amountDueNow = orderTotal - alreadyPaid;
    const nonCashPortion = cardAmount + mobileAmount;
    const cashNeeded = Math.max(0, amountDueNow - nonCashPortion);
    const change = cashAmount > cashNeeded ? cashAmount - cashNeeded : 0;

    return {
      total: orderTotal,          // Total de la commande
      amountDue: amountDueNow,    // Montant à payer (après paiements finalisés)
      alreadyPaid,
      totalPaid,
      remaining,
      change,
      isComplete: totalPaid >= amountDueNow && amountDueNow > 0,
      isOverpaid: totalPaid > amountDueNow
    };
  }, [paymentSession, tableDetails, cashAmount, cardAmount, mobileAmount]);

  // Sauvegarder les montants dans la session backend
  const savePaymentDraft = useCallback(async (cash, card, mobile) => {
    if (!table?.id || !paymentSession) return;
    
    // Vérifier si les valeurs ont changé
    if (
      lastSavedRef.current.cash === cash &&
      lastSavedRef.current.card === card &&
      lastSavedRef.current.mobile === mobile
    ) {
      return; // Pas de changement, ne pas sauvegarder
    }

    setSaving(true);
    try {
      await api().put(`/api/tables/${table.id}/update-payment-draft`, {
        cash,
        card,
        mobile
      });
      lastSavedRef.current = { cash, card, mobile };
      console.log('[PaymentModal] Montants sauvegardés:', { cash, card, mobile });
    } catch (error) {
      console.error('Error saving payment draft:', error);
      // Ne pas afficher de toast pour éviter le spam
    } finally {
      setSaving(false);
    }
  }, [table?.id, paymentSession]);

  // Sauvegarder automatiquement quand les montants changent (debounced)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (paymentSession && (debouncedCash > 0 || debouncedCard > 0 || debouncedMobile > 0)) {
      savePaymentDraft(debouncedCash, debouncedCard, debouncedMobile);
    }
  }, [debouncedCash, debouncedCard, debouncedMobile, savePaymentDraft, paymentSession]);

  // Charger ou démarrer la session de paiement
  const loadOrStartPaymentSession = useCallback(async () => {
    if (!table?.id) return;

    setSessionLoading(true);
    isInitialMount.current = true;
    
    try {
      // D'abord, vérifier si une session existe déjà
      const checkResponse = await api().get(`/api/tables/${table.id}/payment-session`);
      
      if (checkResponse.data.has_session) {
        // Session existante - charger les montants sauvegardés
        setPaymentSession(checkResponse.data);
        
        const savedDraft = checkResponse.data.session?.payments_draft || {};
        setCashAmount(savedDraft.cash || 0);
        setCardAmount(savedDraft.card || 0);
        setMobileAmount(savedDraft.mobile || 0);
        lastSavedRef.current = {
          cash: savedDraft.cash || 0,
          card: savedDraft.card || 0,
          mobile: savedDraft.mobile || 0
        };
        
        console.log('[PaymentModal] Session existante chargée:', savedDraft);
      } else {
        // Pas de session - en créer une nouvelle
        const response = await api().post(`/api/tables/${table.id}/start-payment`);
        setPaymentSession(response.data);
        
        // Initialiser les montants à 0
        setCashAmount(0);
        setCardAmount(0);
        setMobileAmount(0);
        lastSavedRef.current = { cash: 0, card: 0, mobile: 0 };
      }
      
      setNotes('');
      
    } catch (error) {
      console.error('Error loading/starting payment session:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'ouverture du paiement');
      onClose();
    } finally {
      setSessionLoading(false);
    }
  }, [table?.id, onClose]);

  // Charger la session au montage
  useEffect(() => {
    if (open && table?.id) {
      loadOrStartPaymentSession();
    }
  }, [open, table?.id, loadOrStartPaymentSession]);

  // Reset à la fermeture
  useEffect(() => {
    if (!open) {
      setPaymentSession(null);
      setCashAmount(0);
      setCardAmount(0);
      setMobileAmount(0);
      setNotes('');
      isInitialMount.current = true;
      lastSavedRef.current = { cash: 0, card: 0, mobile: 0 };
    }
  }, [open]);

  // Annuler la session de paiement
  const handleCancelSession = async () => {
    if (!table?.id) return;

    setLoading(true);
    try {
      await api().post(`/api/tables/${table.id}/cancel-payment`, {
        reason: 'Annulé par l\'utilisateur'
      });
      toast.info('Paiement annulé');
      onClose();
    } catch (error) {
      console.error('Error canceling payment:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'annulation');
    } finally {
      setLoading(false);
    }
  };

  // Valider le paiement
  const handleProcessPayment = async () => {
    if (!table?.id || !totals.isComplete) return;

    // === RÈGLE 2 & 3: Vérifier que la table est bien en statut "EN_PAIEMENT" ===
    const currentStatus = tableDetails?.status || paymentSession?.table_status;
    if (currentStatus !== 'en_cours_de_paiement') {
      toast.error('Erreur: La table n\'est pas en mode paiement. Veuillez réessayer.', {
        icon: <AlertCircle className="w-5 h-5 text-red-500" />
      });
      onClose();
      return;
    }

    setLoading(true);
    try {
      const response = await api().post(`/api/tables/${table.id}/process-payment`, {
        cash: cashAmount,
        card: cardAmount,
        mobile: mobileAmount,
        notes: notes || null
      });

      toast.success(response.data.message, {
        icon: <CheckCircle className="w-5 h-5 text-green-500" />
      });

      // Afficher la monnaie si applicable
      if (totals.change > 0) {
        toast.info(`Monnaie à rendre: ${formatPrice(totals.change)}`, {
          duration: 5000
        });
      }

      onPaymentSuccess?.(response.data);
      setConfirmDialogOpen(false);
      onClose();

    } catch (error) {
      console.error('Error processing payment:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors du paiement';
      toast.error(errorMessage);
      
      // Si l'erreur est liée au statut, fermer le modal
      if (errorMessage.includes('statut') || errorMessage.includes('status')) {
        onClose();
      }
    } finally {
      setLoading(false);
    }
  };

  // Ajouter un montant rapide aux espèces
  const addQuickAmount = (amount) => {
    setCashAmount(prev => prev + amount);
  };

  // Reset un champ
  const resetField = (field) => {
    switch (field) {
      case 'cash': setCashAmount(0); break;
      case 'card': setCardAmount(0); break;
      case 'mobile': setMobileAmount(0); break;
      default: break;
    }
  };

  // Payer exactement le montant restant
  const payExact = (method) => {
    // Utiliser amountDue (montant à payer après paiements finalisés) au lieu de total
    const amountDue = totals.amountDue ?? totals.total;
    const remaining = amountDue - (
      method === 'cash' ? cardAmount + mobileAmount :
      method === 'card' ? cashAmount + mobileAmount :
      cashAmount + cardAmount
    );
    
    if (remaining > 0) {
      switch (method) {
        case 'cash': setCashAmount(remaining); break;
        case 'card': setCardAmount(remaining); break;
        case 'mobile': setMobileAmount(remaining); break;
        default: break;
      }
    }
  };

  // Items à afficher
  const items = useMemo(() => {
    const draft = paymentSession?.items?.draft || tableDetails?.draft_items || [];
    const sent = paymentSession?.items?.sent || tableDetails?.sent_items || [];
    return [...draft, ...sent];
  }, [paymentSession, tableDetails]);

  if (!open) return null;

  return (
    <>
      <Dialog open={open} onOpenChange={() => {}}>
        <DialogContent className="w-[95vw] max-w-2xl max-h-[95vh] p-0 overflow-hidden" hideCloseButton onPointerDownOutside={(e) => e.preventDefault()}>
          {/* Header - Responsive */}
          <DialogHeader className="p-3 sm:p-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/20 flex items-center justify-center">
                  <Calculator className="w-5 h-5 sm:w-6 sm:h-6" />
                </div>
                <div>
                  <DialogTitle className="text-base sm:text-xl text-white">
                    Table {table?.number}
                  </DialogTitle>
                  <p className="text-green-100 text-xs sm:text-sm">
                    {table?.zone} • {items.length} article(s)
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {/* Indicateur de sauvegarde */}
                {saving && (
                  <div className="hidden sm:flex items-center gap-1 text-green-100 text-xs">
                    <Save className="w-3 h-3 animate-pulse" />
                    <span>Sauvegarde...</span>
                  </div>
                )}
                <Button 
                  variant="ghost" 
                  size="icon" 
                  onClick={handleCancelSession}
                  className="text-white hover:bg-white/20 h-8 w-8 sm:h-10 sm:w-10"
                  disabled={loading}
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </DialogHeader>

          {sessionLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-10 h-10 animate-spin text-green-500" />
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row overflow-hidden" style={{ maxHeight: 'calc(95vh - 80px)' }}>
              {/* Colonne gauche - Récapitulatif (cachée sur mobile, visible en haut sur tablette) */}
              <div className="hidden sm:block w-full lg:w-2/5 border-b lg:border-b-0 lg:border-r bg-gray-50 p-3 sm:p-4 overflow-y-auto max-h-[30vh] lg:max-h-none">
                <h3 className="font-semibold text-sm text-gray-600 mb-2 sm:mb-3">RÉCAPITULATIF</h3>
                
                {/* Liste des articles */}
                <div className="space-y-2 mb-4">
                  {items.map((item, idx) => (
                    <div key={item.id || idx} className="flex justify-between text-sm py-1">
                      <span className="text-gray-700">
                        {item.quantity}x {item.product_name}
                      </span>
                      <span className="font-medium">
                        {formatPrice(item.line_total || item.unit_price * item.quantity)}
                      </span>
                    </div>
                  ))}
                </div>

                <Separator className="my-2 sm:my-3" />

                {/* Total - Visible sur mobile aussi */}
                <div className="bg-white rounded-xl p-3 sm:p-4 border-2 border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-base sm:text-lg font-bold text-gray-800">TOTAL</span>
                    <span className="text-xl sm:text-2xl font-black text-green-600">
                      {formatPrice(totals.total)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Colonne droite - Paiement */}
              <div className="w-full lg:w-3/5 p-3 sm:p-4 overflow-y-auto">
                {/* Total sur mobile uniquement */}
                <div className="sm:hidden bg-green-50 rounded-xl p-3 border border-green-200 mb-3">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-800">À payer</span>
                    <span className="text-xl font-black text-green-600">
                      {formatPrice(totals.total)}
                    </span>
                  </div>
                </div>

                {/* Méthodes de paiement */}
                <div className="space-y-3 sm:space-y-4">
                  {/* ESPÈCES */}
                  <div className="bg-amber-50 rounded-xl p-3 sm:p-4 border border-amber-200">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <div className="flex items-center gap-2">
                        <Banknote className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600" />
                        <span className="font-semibold text-sm sm:text-base text-amber-800">Espèces</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => payExact('cash')}
                          className="text-xs text-amber-600 hover:bg-amber-100 h-7 px-2"
                        >
                          Exact
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetField('cash')}
                          className="text-xs text-amber-600 hover:bg-amber-100 h-7 px-2"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <Input
                      type="number"
                      value={cashAmount || ''}
                      onChange={(e) => setCashAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="text-xl sm:text-2xl font-bold text-center h-12 sm:h-14 mb-2 sm:mb-3"
                      placeholder="0"
                    />
                    
                    {/* Boutons rapides - Responsive grid */}
                    <div className="grid grid-cols-3 sm:grid-cols-6 gap-1.5 sm:gap-2">
                      {QUICK_AMOUNTS.map(amount => (
                        <Button
                          key={amount}
                          variant="outline"
                          onClick={() => addQuickAmount(amount)}
                          className="text-xs sm:text-sm font-semibold border-amber-300 hover:bg-amber-100 h-9 sm:h-10 px-1 sm:px-3"
                        >
                          +{amount >= 1000 ? `${amount/1000}k` : amount}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* CARTE BANCAIRE */}
                  <div className="bg-blue-50 rounded-xl p-3 sm:p-4 border border-blue-200">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 text-blue-600" />
                        <span className="font-semibold text-sm sm:text-base text-blue-800">Carte</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => payExact('card')}
                          className="text-xs text-blue-600 hover:bg-blue-100 h-7 px-2"
                        >
                          Exact
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetField('card')}
                          className="text-xs text-blue-600 hover:bg-blue-100 h-7 px-2"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <Input
                      type="number"
                      value={cardAmount || ''}
                      onChange={(e) => setCardAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="text-xl sm:text-2xl font-bold text-center h-12 sm:h-14"
                      placeholder="0"
                    />
                  </div>

                  {/* MOBILE MONEY */}
                  <div className="bg-purple-50 rounded-xl p-3 sm:p-4 border border-purple-200">
                    <div className="flex items-center justify-between mb-2 sm:mb-3">
                      <div className="flex items-center gap-2">
                        <Smartphone className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                        <span className="font-semibold text-sm sm:text-base text-purple-800">Mobile</span>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => payExact('mobile')}
                          className="text-xs text-purple-600 hover:bg-purple-100 h-7 px-2"
                        >
                          Exact
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => resetField('mobile')}
                          className="text-xs text-purple-600 hover:bg-purple-100 h-7 px-2"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                    
                    <Input
                      type="number"
                      value={mobileAmount || ''}
                      onChange={(e) => setMobileAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="text-xl sm:text-2xl font-bold text-center h-12 sm:h-14"
                      placeholder="0"
                    />
                  </div>
                </div>

                {/* Résumé du paiement - Responsive */}
                <div className={cn(
                  "mt-3 sm:mt-4 rounded-xl p-3 sm:p-4 border-2 transition-colors",
                  totals.isComplete 
                    ? "bg-green-50 border-green-300" 
                    : "bg-red-50 border-red-300"
                )}>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Total:</span>
                      <span className="font-bold">{formatPrice(totals.total)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Payé:</span>
                      <span className={cn(
                        "font-bold",
                        totals.isComplete ? "text-green-600" : "text-amber-600"
                      )}>
                        {formatPrice(totals.totalPaid)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reste:</span>
                      <span className={cn(
                        "font-bold",
                        totals.remaining > 0 ? "text-red-600" : "text-green-600"
                      )}>
                        {formatPrice(totals.remaining)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monnaie:</span>
                      <span className={cn(
                        "font-bold",
                        totals.change > 0 ? "text-blue-600" : "text-gray-400"
                      )}>
                        {formatPrice(totals.change)}
                      </span>
                    </div>
                  </div>

                  {/* Indicateur de statut */}
                  <div className="mt-2 sm:mt-3 flex items-center justify-center gap-2">
                    {totals.isComplete ? (
                      <>
                        <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-600" />
                        <span className="font-semibold text-sm sm:text-base text-green-700">Paiement complet</span>
                      </>
                    ) : (
                      <>
                        <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600" />
                        <span className="font-semibold text-sm sm:text-base text-red-700">
                          Manque {formatPrice(totals.remaining)}
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {/* Boutons d'action - Responsive */}
                <div className="mt-3 sm:mt-4 flex gap-2 sm:gap-3">
                  <Button
                    variant="outline"
                    onClick={handleCancelSession}
                    className="flex-1 h-11 sm:h-12"
                    disabled={loading}
                  >
                    <ArrowLeft className="w-4 h-4 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">Annuler</span>
                    <span className="sm:hidden">Retour</span>
                  </Button>
                  
                  <Button
                    onClick={() => setConfirmDialogOpen(true)}
                    className={cn(
                      "flex-1 text-base sm:text-lg h-11 sm:h-14",
                      totals.isComplete 
                        ? "bg-green-600 hover:bg-green-700" 
                        : "bg-gray-400 cursor-not-allowed"
                    )}
                    disabled={!totals.isComplete || loading}
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin mr-2" />
                    ) : (
                      <CheckCircle className="w-5 h-5 mr-2" />
                    )}
                    Valider
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmation */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Confirmer l'encaissement ?</DialogTitle>
          </DialogHeader>
          
          <div className="py-4 space-y-3">
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span>Total:</span>
                <span className="font-bold">{formatPrice(totals.total)}</span>
              </div>
              {cashAmount > 0 && (
                <div className="flex justify-between text-amber-700">
                  <span>Espèces:</span>
                  <span>{formatPrice(cashAmount)}</span>
                </div>
              )}
              {cardAmount > 0 && (
                <div className="flex justify-between text-blue-700">
                  <span>Carte:</span>
                  <span>{formatPrice(cardAmount)}</span>
                </div>
              )}
              {mobileAmount > 0 && (
                <div className="flex justify-between text-purple-700">
                  <span>Mobile:</span>
                  <span>{formatPrice(mobileAmount)}</span>
                </div>
              )}
              {totals.change > 0 && (
                <div className="flex justify-between text-green-700 font-bold border-t pt-2 mt-2">
                  <span>Monnaie à rendre:</span>
                  <span>{formatPrice(totals.change)}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setConfirmDialogOpen(false)}
              className="flex-1"
              disabled={loading}
            >
              Retour
            </Button>
            <Button
              onClick={handleProcessPayment}
              className="flex-1 bg-green-600 hover:bg-green-700"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : (
                <CheckCircle className="w-5 h-5 mr-2" />
              )}
              Confirmer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentModal;
